import React, { useState, useEffect } from 'react';
import { useTagSuggestions, useUpdateInvoiceTags, extractTagName } from '../../api/useTags';
import { extractStringArray } from '../../utils/sanitizeData';

interface EditTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicToken?: string;
  initialTags?: unknown[];
  onSaveTags?: (tags: string[]) => void;
}

export default function EditTagsModal({
  isOpen,
  onClose,
  publicToken,
  initialTags = [],
  onSaveTags,
}: EditTagsModalProps) {
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [inputVal, setInputVal] = useState('');

  const { data: suggestionsData } = useTagSuggestions();
  const updateTagsMutation = useUpdateInvoiceTags();

  useEffect(() => {
    if (isOpen) {
      setActiveTags(extractStringArray(initialTags));
      setInputVal('');
    }
  }, [isOpen, initialTags]);

  if (!isOpen) return null;

  const topUsed = (suggestionsData?.top_tags || suggestionsData?.top_used || suggestionsData?.tags || []).map(extractTagName).filter(Boolean);
  const recentlyUsed = (suggestionsData?.recent_tags || suggestionsData?.recently_used || []).map(extractTagName).filter(Boolean);
  const aiSuggestions = (suggestionsData?.ai_suggestions || []).map(extractTagName).filter(Boolean);

  const handleAddTag = (raw: string) => {
    const cleaned = raw.trim().replace(/^#+/, '');
    if (cleaned && !activeTags.includes(cleaned)) {
      setActiveTags([...activeTags, cleaned]);
      setInputVal('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setActiveTags(activeTags.filter((t) => t !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag(inputVal);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputVal.trim()) {
      handleAddTag(inputVal);
    }

    const finalTags = [...activeTags];
    if (inputVal.trim() && !finalTags.includes(inputVal.trim())) {
      finalTags.push(inputVal.trim());
    }

    onSaveTags?.(finalTags);

    if (publicToken) {
      try {
        await updateTagsMutation.mutateAsync({
          publicToken,
          tags: finalTags,
        });
      } catch {
        // Safe fallback in dev / before backend endpoint rollout
      }
    }

    onClose();
  };

  const isSaving = updateTagsMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
      <div
        className="w-full max-w-lg rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 sm:p-7 relative animate-fadeIn"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-brand-line dark:border-strokedark mb-5">
          <div>
            <h2 className="text-xl font-bold text-brand-navy dark:text-white tracking-tight">
              Edit Classification Tags
            </h2>
            <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
              Add or remove custom tags and AI suggestions for this invoice.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-brand-soft hover:text-brand-navy dark:hover:text-white hover:bg-brand-bg dark:hover:bg-strokedark transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          {/* Active Tags Badge List */}
          <div>
            <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-2">
              Active Tags ({activeTags.length})
            </label>
            <div className="min-h-[42px] p-2 rounded-xl bg-brand-bg/60 dark:bg-strokedark/30 border border-brand-line dark:border-strokedark flex flex-wrap items-center gap-1.5">
              {activeTags.length > 0 ? (
                activeTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 border border-brand-vib/20"
                  >
                    <span>#{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer"
                      title="Remove tag"
                    >
                      ✕
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-xs font-mono text-brand-soft italic pl-1">
                  No tags added yet. Type below or click suggestions.
                </span>
              )}
            </div>
          </div>

          {/* Add Tag Input */}
          <div>
            <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-2">
              Add New Tag
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                autoFocus
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type tag name and press Enter (e.g. Marketing, O365)..."
                className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
              />
              <button
                type="button"
                onClick={() => handleAddTag(inputVal)}
                disabled={!inputVal.trim()}
                className="px-4 py-2.5 text-xs font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-vib dark:hover:text-white rounded-xl border border-brand-vib/20 disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap"
              >
                + Add
              </button>
            </div>
          </div>

          {/* Section: AI Suggestions */}
          {aiSuggestions.length > 0 && (
            <div>
              <div className="text-xs font-bold text-brand-navy dark:text-white mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#029664]" />
                <span>AI Suggestions</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {aiSuggestions.map((tag) => {
                  const isSelected = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => (isSelected ? handleRemoveTag(tag) : handleAddTag(tag))}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors shadow-xs cursor-pointer ${
                        isSelected
                          ? 'bg-[#029664] text-white'
                          : 'bg-emerald-50 text-[#029664] border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 hover:bg-[#029664] hover:text-white'
                      }`}
                    >
                      {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Top Used */}
          {topUsed.length > 0 && (
            <div>
              <div className="text-xs font-bold text-brand-navy dark:text-white mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#114FEE]" />
                <span>Top Used Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {topUsed.map((tag) => {
                  const isSelected = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => (isSelected ? handleRemoveTag(tag) : handleAddTag(tag))}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors shadow-xs cursor-pointer ${
                        isSelected
                          ? 'bg-[#114FEE] text-white'
                          : 'bg-blue-50 text-[#114FEE] border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300 hover:bg-[#114FEE] hover:text-white'
                      }`}
                    >
                      {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Recently Used */}
          {recentlyUsed.length > 0 && (
            <div>
              <div className="text-xs font-bold text-brand-navy dark:text-white mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6C11EE]" />
                <span>Recently Used</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentlyUsed.map((tag) => {
                  const isSelected = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => (isSelected ? handleRemoveTag(tag) : handleAddTag(tag))}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors shadow-xs cursor-pointer ${
                        isSelected
                          ? 'bg-[#6C11EE] text-white'
                          : 'bg-purple-50 text-[#6C11EE] border border-purple-200 dark:bg-purple-950/40 dark:border-purple-800 dark:text-purple-300 hover:bg-[#6C11EE] hover:text-white'
                      }`}
                    >
                      {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-brand-line dark:border-strokedark mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark hover:bg-brand-bg dark:hover:bg-strokedark transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover disabled:opacity-50 transition-colors shadow-sm cursor-pointer"
            >
              {isSaving ? 'Saving Tags…' : 'Save Tags'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
